import { css, keyframes } from "@emotion/css";
import styled from "@emotion/styled";
import { BorderRadius, Card, Colors, Fonts, Spacing } from "@snapchat/snap-design-system";

const { Paragraph, SubHeading } = Fonts;

export const CreateAssetContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

export const NoteLine = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;

    margin: 4px 0;

    ${Paragraph.P200};
    color: ${Colors.Black.V50};
`;

export const CardContent = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
`;

export const StatusCard = styled(Card)`
    .sds-card-body {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

export const InfoCallout = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-start;

    padding: 12px;

    background: ${Colors.Gray.V50};
    border: 1px solid ${Colors.Gray.V150};
    border-radius: ${BorderRadius.container}px;

    svg {
        flex-shrink: 0;
        margin-top: 1px;
    }
`;

export const InfoCalloutText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    ${Paragraph.P300};
    color: ${Colors.Black.V100};
`;

export const FormLayout = styled.div`
    display: flex;
    gap: 24px;
    align-items: flex-start;
`;

export const SubmitRow = styled.div`
    display: flex;
    justify-content: flex-end;
`;

export const FormColumn = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 16px;

    min-width: 0;
`;

export const PreviewColumn = styled.div`
    flex-shrink: 0;
    width: 252px;
`;

export const PreviewCardRoot = styled.div`
    overflow: hidden;
    background: #2b2e33;
    border: 1px solid #3b4754;
    border-radius: ${BorderRadius.container}px;
`;

export const PreviewImageArea = styled.div`
    position: relative;

    display: flex;
    align-items: center;
    justify-content: center;

    aspect-ratio: 1 / 1;

    background: #3a3e44;
`;

export const PreviewImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

export const PreviewImagePlaceholder = styled.div`
    ${Paragraph.P300};
    color: #8a8f96;
`;

export const PreviewMenu = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 28px;
    height: 28px;

    color: #fff;

    background: rgba(0, 0, 0, 45%);
    border-radius: ${BorderRadius.container}px;
`;

export const PreviewBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
`;

export const PreviewTitle = styled.div`
    ${SubHeading.SH200};
    overflow: hidden;
    color: #fff;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const PreviewCreator = styled.div`
    ${Paragraph.P300};
    color: #c7c7cc;
`;

export const PreviewPills = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

export const PreviewPlatformTag = styled.span`
    padding: 4px 12px;
    ${Paragraph.P300};
    color: #e9eaeb;
    background: #16181b;
    border-radius: 100px;
`;

export const PreviewSize = styled.div`
    ${Paragraph.P300};
    color: #8a8f96;
`;

export const PreviewDescription = styled.div`
    max-width: 260px;

    p {
        margin: 0;
    }

    > *:first-child {
        margin-top: 0;
    }

    > *:last-child {
        margin-bottom: 0;
    }
`;

export const PreviewBottomRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
`;

export const PreviewInfoButton = styled.button`
    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 40px;
    height: 40px;

    color: #fff;

    background: transparent;
    border: none;
    border-radius: ${BorderRadius.container}px;
`;

export const PreviewInstallButton = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;

    padding: 8px 16px;

    ${Paragraph.P300};
    font-weight: 600;
    color: #fff;

    background: #00b6ff;
    border-radius: ${BorderRadius.container}px;
`;

export const StatusText = styled.div`
    display: flex;
    flex-direction: column;
`;

export const StatusHeading = styled.h2`
    ${SubHeading.SH200};
    margin: 0;
    color: ${Colors.Black.V200};
`;

export const StyledSubtitle = styled.p`
    ${Paragraph.P300};
    color: ${Colors.Black.V50};
`;

export const StatusSubHeading = styled.p`
    ${Paragraph.P300};
    margin: 0;
    color: ${Colors.Black.V50};
`;

export const ButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
    margin-top: 4px;

    /* match the category chip radius (&& wins over SDS's .sds-button.ant-btn) */

    && .sds-button {
        min-width: 0;
        border-radius: ${BorderRadius.container}px;
    }
`;

export const FileName = styled.span`
    display: inline-flex;
    gap: 4px;
    align-items: center;

    min-width: 0;

    ${Paragraph.P300};
    color: ${Colors.Black.V50};

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    svg {
        flex-shrink: 0;
    }
`;

export const StyledCard = styled(Card)`
    .sds-card-body {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .sds-control-label:first-of-type {
        margin-bottom: 8px;
    }
`;

export const LinkVersionContainer = styled.div`
    display: flex;
    gap: 16px;
`;

export const UploadsRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;

    > * {
        min-width: 0;
    }
`;

export const TypeHintList = styled.ul`
    margin: 0;
    padding-left: 16px;

    li + li {
        margin-top: 4px;
    }
`;

export const CategoryPills = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .sds-tag {
        cursor: pointer;

        /* square the SDS pill (radius 100) to match the panel/container radius */
        border-radius: ${BorderRadius.container}px;
    }
`;

/* selected category chip: muted grey fill (kept low-contrast vs the old solid black) */
export const selectedChipCss = css`
    && {
        color: ${Colors.Black.V200};
        background: ${Colors.Gray.V150};
        border-color: ${Colors.Gray.V250};
    }
`;

export const hideInput = css`
    position: absolute;
    z-index: -1;

    overflow: hidden;

    width: 0.1px;
    height: 0.1px;

    opacity: 0;
`;

export const requiredCss = css`
    .sds-control-label:first-of-type {
        &:after {
            content: "\\00a0*";
            color: ${Colors.Red.V200};
        }
    }
`;

export const ErrorWrapper = styled.div`
    display: flex;
    gap: ${Spacing["1XS"]}px;
    margin-top: ${Spacing["1XS"]}px;

    svg {
        margin-top: ${Spacing["1XS"]}px;
    }
`;

export const RichTextWrapper = styled.div`
    overflow: hidden;
    border: 1px solid ${Colors.Gray.V250};
    border-radius: ${BorderRadius.container}px;

    &:focus-within {
        border-color: ${Colors.Black.V50};
    }

    .ql-toolbar.ql-snow {
        background: ${Colors.Gray.V50};
        border: none;
        border-bottom: 1px solid ${Colors.Gray.V250};
    }

    .ql-container.ql-snow {
        ${Paragraph.P300};
        border: none;
    }

    .ql-editor {
        min-height: 120px;

        a {
            color: ${Colors.Blue.V200};
        }
    }

    .ql-editor.ql-blank:before {
        font-style: normal;
        color: ${Colors.Black.V50};
    }
`;

export const richTextErrorCss = css`
    && {
        border-color: ${Colors.Red.V200};
    }
`;

/* ---- Status header progress donut ---- */
export const ProgressDonut = styled.div<{ $accent: string; $pct: number }>`
    position: relative;

    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;

    width: 52px;
    height: 52px;

    background: conic-gradient(
        ${({ $accent }) => $accent} ${({ $pct }) => $pct}%,
        ${Colors.Gray.V150} ${({ $pct }) => $pct}%
    );
    border-radius: 50%;

    &:after {
        content: "";

        position: absolute;
        inset: 7px;

        background: ${Colors.WHITE};
        border-radius: 50%;
    }
`;

export const ProgressDonutLabel = styled.span`
    position: relative;
    z-index: 1;
    ${Paragraph.P300};
    font-weight: 700;
    color: ${Colors.Black.V200};
`;

/* ---- Error summary + success banners ---- */
const shake = keyframes`
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-6px); }
    40% { transform: translateX(6px); }
    60% { transform: translateX(-4px); }
    80% { transform: translateX(4px); }
`;

export const ErrorSummary = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    padding: 16px 20px;

    background: rgba(242, 60, 87, 6%);
    border: 1px solid rgba(242, 60, 87, 35%);
    border-radius: ${BorderRadius.container}px;

    animation: ${shake} 0.5s;
`;

export const ErrorSummaryTitle = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    ${SubHeading.SH200};
    color: ${Colors.Red.V200};
`;

export const ErrorSummaryList = styled.ul`
    margin: 0;
    padding-left: 20px;
    ${Paragraph.P300};
    color: ${Colors.Red.V200};

    li + li {
        margin-top: 4px;
    }
`;

/* ---- Derived ("from Type") category chip ---- */
export const DerivedChip = styled.span`
    cursor: default;

    display: inline-flex;
    gap: ${Spacing["2XS"]}px;
    align-items: center;

    padding: ${Spacing["1XS"]}px ${Spacing.SM}px;

    ${Paragraph.P300};
    font-weight: 500;
    color: ${Colors.Black.V200};

    background: ${Colors.Gray.V150};
    border: 1px solid ${Colors.Gray.V250};
    border-radius: ${BorderRadius.container}px;

    svg {
        flex-shrink: 0;
    }
`;

export const DerivedCaption = styled.div`
    margin-top: 8px;
    ${Paragraph.P300};
    color: ${Colors.Black.V50};
`;

/* ---- Drag & drop file zone ---- */
export const DropZone = styled.div<{ $state: "idle" | "dragover" | "error" | "filled" }>`
    cursor: pointer;

    position: relative;

    display: flex;
    gap: 16px;
    align-items: center;

    padding: 18px 20px;

    border-radius: ${BorderRadius.container}px;

    transition:
        border-color 0.2s ease,
        background 0.2s ease;

    ${({ $state }) => {
        switch ($state) {
            case "dragover":
                return `border: 1.5px dashed ${Colors.Blue.V200}; background: rgba(0, 182, 255, 0.06);`;
            case "error":
                return `border: 1.5px solid ${Colors.Red.V200}; background: rgba(242, 60, 87, 0.04);`;
            case "filled":
                return `border: 1.5px solid ${Colors.Green.V200}; background: rgba(57, 202, 142, 0.05);`;
            default:
                return `border: 1.5px dashed ${Colors.Gray.V250}; background: ${Colors.WHITE};`;
        }
    }}
`;

// Full-area invisible button so the drop zone is keyboard- and screen-reader-operable.
export const DropZoneTrigger = styled.button`
    cursor: pointer;

    position: absolute;
    inset: 0;

    background: transparent;
    border: none;
    border-radius: ${BorderRadius.container}px;

    &:focus-visible {
        outline: 2px solid ${Colors.Blue.V200};
        outline-offset: 2px;
    }
`;

export const DropZoneIconTile = styled.div<{ $filled?: boolean }>`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;

    width: 42px;
    height: 42px;

    color: ${({ $filled }) => ($filled ? Colors.WHITE : Colors.Black.V50)};

    background: ${({ $filled }) => ($filled ? Colors.Green.V200 : Colors.Gray.V50)};
    border-radius: 10px;
`;

export const DropZoneThumb = styled.img`
    flex-shrink: 0;

    width: 42px;
    height: 42px;

    object-fit: cover;
    border-radius: 10px;
`;

export const DropZoneContent = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;

    min-width: 0;
`;

export const DropZonePrompt = styled.div`
    ${Paragraph.P300};
    color: ${Colors.Black.V100};

    .browse {
        font-weight: 600;
        color: ${Colors.Blue.V200};
    }
`;

export const DropZoneHint = styled.div`
    ${Paragraph.P300};
    color: ${Colors.Black.V50};
`;

export const DropZoneFileRow = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;

    min-width: 0;

    ${Paragraph.P300};
    color: ${Colors.Black.V100};

    .filename {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    svg {
        flex-shrink: 0;
    }
`;

export const DropZoneSize = styled.span`
    flex-shrink: 0;
    ${Paragraph.P300};
    color: ${Colors.Black.V50};
`;

export const RemoveButton = styled.button`
    cursor: pointer;

    position: relative;
    z-index: 1;

    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;

    width: 28px;
    height: 28px;
    margin-left: auto;
    padding: 0;

    color: ${Colors.Black.V50};

    background: ${Colors.Gray.V50};
    border: none;
    border-radius: 8px;

    &:hover {
        background: ${Colors.Gray.V150};
    }
`;
